// push-item-content.component.ts
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RadioLabelComponent } from './radio-label.component';
import { CheckboxLabelComponent } from './checkbox-label.component';
import { OptionContentComponent } from './option-content.component';
import { SeparatorComponent } from './separator.component';
import {TranslateService} from '../../services/translate.service';
import {OrderService} from '../../services/order.service';
import {MenuItem, PickedPackage} from '../../models/FSMenu';
import MoneyUtils from '../../utils/Utils';

interface ContentItem {
  name: string;
  del?: boolean;
}

interface Item {
  id: string;
  name: string;
  desc?: string;
  contains?: ContentItem[];
  attributes?: Attribute[];
}

interface Attribute {
  id: string;
  options: Option[];
}

interface Option {
  key: string;
  value: string;
  formatted?: string;
  contains?: ContentItem[];
}

@Component({
  selector: 'app-push-item-content',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RadioLabelComponent,
    CheckboxLabelComponent,
    OptionContentComponent,
    SeparatorComponent
  ],
  template: `
    <div class="flex-grow w-full">
      <!-- Item header -->
      <div class="flex justify-between items-center text-theme-text">
        <span class="font-medium text-lg">{{getString(item.name)}}</span>
        <span *ngIf="!itemIncluded" class="tabular-nums text-end">{{getItemPriceFormatted(item)}}</span>
      </div>

      <!-- A package's picks, chosen in the item view -->
      <div *ngIf="selectedItems[item.id] && picked[item.id] as pk" class="mt-2 text-theme-text2">
        <div *ngFor="let line of pk.lines">{{line}}</div>
        <button type="button" class="mt-1 font-bold text-theme-primary underline"
                (click)="$event.preventDefault(); $event.stopPropagation(); changePicks.emit(item)">{{getString("Ändra")}}</button>
      </div>

      <!-- Item description -->
      <div *ngIf="item.desc" class="opacity-75">
        {{getString(item.desc)}}
      </div>

      <!-- Item contents -->
      <div *ngIf="item.contains" class="opacity-75 font-light">
        {{getString("INNEHÅLLER", "upper")}}
        <span *ngFor="let con of item.contains; let last = last">
          {{getString(con.name, "upper")}}{{last ? "" : ", "}}
        </span>
      </div>

      <!-- Attributes section -->
      <div *ngIf="selectedItems[item.id] && item.attributes && !picked[item.id]" class="mt-3 space-y-4">
        <div *ngFor="let attribute of item.attributes; let lastatr = last">

          <!-- Radio group -->
          <div *ngIf="attribute.options.length > 1" class="space-y-2">
            <div *ngFor="let option of attribute.options">
              <app-radio-label
                [(ngModel)]="attrValues[item.id][attribute.id]"
                [radioValue]="option.key">
                <app-option-content
                  class="flex-1"
                  [option]="option"
                  [itemIncluded]="itemIncluded"
                  [isLarge]="true"
                  [regex]="regex">
                </app-option-content>
              </app-radio-label>
            </div>
          </div>

          <!-- Single checkbox -->
          <div *ngIf="attribute.options.length === 1">
            <app-checkbox-label [(ngModel)]="attrValues[item.id][attribute.id]">
              <app-option-content
                class="flex-1"
                [option]="attribute.options[0]"
                [itemIncluded]="itemIncluded">
              </app-option-content>
            </app-checkbox-label>
          </div>

          <!-- Separator -->
          <app-separator *ngIf="!lastatr"></app-separator>
        </div>
      </div>
    </div>
  `
})
export class PushItemContentComponent {
  @Input() item!: MenuItem;
  @Input() itemIncluded = false;
  @Input() selectedItems: { [key: string]: boolean } = {};
  @Input() attrValues: { [key: string]: { [key: string]: any } } = {};
  @Input() regex?: RegExp;
  // Packages picked in the item view, by item id: shown with their picks and price instead of the attributes
  @Input() picked: Record<string, PickedPackage> = {};
  @Output() changePicks = new EventEmitter<MenuItem>();

  constructor(private translate: TranslateService, private orderService: OrderService) {}

  getString(value: string, pc?: string): string {
    return this.translate.getString(value, pc);
  }

  getItemPriceFormatted(item: MenuItem): string {
    const pk = this.selectedItems[item.id] ? this.picked[item.id] : undefined;
    // A takeaway price is the menu's own; the item view shows that one too
    if (pk && !this.orderService.isShowingTakeawayPrice(item)) {
      return MoneyUtils.formatNoCents(pk.price);
    }
    return this.orderService.getPriceForItem(item);
  }
}
